// The signal picker (custom + conditional alarms, phase 5): choose ONE path out
// of everything the boat actually reports. It is the front door of the custom
// builder, and phase 6 reuses it unchanged for each condition clause — hence the
// deliberately small prop surface (signals in, a pick out) and the caller-chosen
// `testidPrefix`.
//
// What it offers is exactly `GET /v1/boats/:boatId/signals` — the accepted-path
// set the cloud compiler binds against. It never invents an entry, so anything
// you can choose here is something the boat can actually watch.
//
// Each row states the same four things: the human name (plus the device in
// parentheses when we know it), the raw path in mono so a technical user can
// confirm the exact binding, the current reading in the boat's own units, and a
// freshness dot on the same bands as the Devices screen. The reading prefers the
// live WebSocket feed over the catalog snapshot (`signalLive.ts`), so a value
// ticking on the Instruments page ticks here too.
//
// Interaction: a trigger button opens a search + grouped listbox. Desktop gets a
// popover under the trigger; at 640px and below the panel becomes a full-screen
// sheet with the search pinned to the top, because a dropdown inside a phone
// form is unusable. Keyboard: type to filter, arrows to move, Enter to pick,
// Escape to close — the active row is published through `aria-activedescendant`
// so focus never leaves the search box.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { formatRelativeShort } from '../../lib/deviceSeen';
import {
  freshness,
  searchHaystack,
  signalGroup,
  signalDisplayName,
  type Signal,
  type SignalGroup,
} from '../../lib/signalCatalog';
import { categoryFor } from '../../lib/displayUnits';
import { formatReading, signalReading } from '../../lib/signalLive';
import { useUnits } from '../../lib/useUnits';
import { presenceDotClass, useBoatChrome } from '../app/boatChrome';

/**
 * Group order for the list: the things people alarm on most, first. Anything a
 * new group name lands in still renders — {@link signalGroup} only ever returns
 * one of these.
 */
const GROUP_ORDER: readonly SignalGroup[] = [
  'Engine',
  'Batteries',
  'Tanks',
  'Electrical',
  'Switches',
  'Environment',
  'Navigation',
  'Steering',
  'Derived',
  'Other',
];

/** Re-render cadence for the freshness dots and "last reported" wording. */
const FRESHNESS_TICK_MS = 30_000;

export interface SignalPickerProps {
  /** Everything the boat reports — the catalog, already loaded by the caller. */
  signals: readonly Signal[];
  /** The pinned path, or undefined when nothing is picked yet. */
  value?: string;
  onPick: (signal: Signal) => void;
  /** Field label, e.g. "Signal" or (phase 6) "When this signal…". */
  label: string;
  /** Root test id; rows are `${testidPrefix}-row-<path>`, search `-search`. */
  testidPrefix: string;
}

interface GroupedSignals {
  group: SignalGroup;
  rows: Signal[];
}

function labelOf(signal: Signal): string {
  return signalDisplayName(signal);
}

/** Every signal whose haystack contains every whitespace-separated term. */
function filterSignals(signals: readonly Signal[], query: string): Signal[] {
  const terms = query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  if (terms.length === 0) return [...signals];
  return signals.filter((s) => {
    const hay = searchHaystack(s);
    return terms.every((t) => hay.includes(t));
  });
}

export function SignalPicker({ signals, value, onPick, label, testidPrefix }: SignalPickerProps) {
  // The account's display units (ADR 0189). Every reading this picker prints —
  // the rows AND the chosen signal on the trigger button — goes through them,
  // because the button sits directly above a set-point box that converts: "2.5 m"
  // over a box reading "8.2 ft" is one signal in two units, inches apart. The
  // category comes from each signal's own PATH, the same resolution the box and
  // every other live row use.
  const units = useUnits();
  const { liveSamples } = useBoatChrome();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());

  const domId = useId();
  const listId = `${domId}-list`;
  const labelId = `${domId}-label`;
  const valueId = `${domId}-value`;
  const optionId = (index: number): string => `${domId}-opt-${index}`;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), FRESHNESS_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const grouped = useMemo<GroupedSignals[]>(() => {
    const matches = filterSignals(signals, query);
    const byGroup = new Map<SignalGroup, Signal[]>();
    for (const signal of matches) {
      const group = signalGroup(signal.path);
      const rows = byGroup.get(group);
      if (rows === undefined) byGroup.set(group, [signal]);
      else rows.push(signal);
    }
    const out: GroupedSignals[] = [];
    for (const group of GROUP_ORDER) {
      const rows = byGroup.get(group);
      if (rows === undefined || rows.length === 0) continue;
      rows.sort((a, b) => labelOf(a).localeCompare(labelOf(b)) || a.path.localeCompare(b.path));
      out.push({ group, rows });
    }
    return out;
  }, [signals, query]);

  // The rows in visual order — what the arrow keys walk — plus each path's
  // position in it, so a row can render its own option id without scanning.
  const flat = useMemo(() => grouped.flatMap((g) => g.rows), [grouped]);
  const indexByPath = useMemo(() => {
    const map = new Map<string, number>();
    flat.forEach((signal, index) => map.set(signal.path, index));
    return map;
  }, [flat]);
  const selected = useMemo(
    () => (value === undefined ? undefined : signals.find((s) => s.path === value)),
    [signals, value]
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }, []);

  function openPanel(): void {
    const index = value === undefined ? -1 : flat.findIndex((s) => s.path === value);
    setActiveIndex(index >= 0 ? index : 0);
    setQuery('');
    setOpen(true);
  }

  function pick(signal: Signal): void {
    onPick(signal);
    close();
  }

  // Focus the search as the panel opens, so typing filters immediately.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(id);
  }, [open]);

  // A click outside closes without picking (matches every other menu here).
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      const root = rootRef.current;
      if (root !== null && e.target instanceof Node && !root.contains(e.target)) close();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Keep the highlighted row on screen while the arrows walk the list.
  useEffect(() => {
    if (!open) return;
    const path = flat[activeIndex]?.path;
    if (path === undefined) return;
    rowRefs.current.get(path)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, flat]);

  function onSearch(next: string): void {
    setQuery(next);
    setActiveIndex(0);
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flat.length === 0 ? 0 : Math.min(flat.length - 1, i + 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActiveIndex(Math.max(0, flat.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const signal = flat[activeIndex];
      if (signal !== undefined) pick(signal);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }

  function renderReading(signal: Signal): {
    text: string;
    dot: string;
    seen: string;
  } {
    const reading = signalReading(signal, liveSamples?.get(signal.path));
    const text =
      formatReading(reading, categoryFor(signal.path, reading.unit), units) ?? 'No reading';
    const dot = presenceDotClass(freshness(reading.atIso, now));
    const relative = reading.atIso === null ? null : formatRelativeShort(reading.atIso, now);
    return { text, dot, seen: relative === null ? 'Never reported' : `Last reported ${relative}` };
  }

  const trigger = selected === undefined ? null : renderReading(selected);

  return (
    <div className="signal-picker" data-testid={testidPrefix} ref={rootRef}>
      <span className="signal-picker__label" id={labelId}>
        {label}
      </span>

      <button
        type="button"
        ref={triggerRef}
        className={`signal-picker__trigger${selected === undefined ? '' : ' signal-picker__trigger--chosen'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${labelId} ${valueId}`}
        data-testid={`${testidPrefix}-trigger`}
        onClick={() => (open ? close() : openPanel())}
      >
        <span className="signal-picker__chosen" id={valueId}>
          {selected === undefined ? (
            <span className="signal-picker__placeholder">Choose a signal</span>
          ) : (
            <>
              <span className="signal-picker__chosen-label">
                {labelOf(selected)}
                {selected.deviceName === null || selected.deviceName === '' ? null : (
                  <span className="signal-picker__device"> ({selected.deviceName})</span>
                )}
              </span>
              <span className="signal-picker__path">{selected.path}</span>
            </>
          )}
        </span>
        {trigger === null ? null : (
          <span className="signal-picker__live" title={trigger.seen}>
            <span
              className={`boat-status-dot boat-status-dot--${trigger.dot}`}
              aria-hidden="true"
            />
            <span className="signal-picker__value">{trigger.text}</span>
          </span>
        )}
        <span className="signal-picker__action" aria-hidden="true">
          {selected === undefined ? 'Choose' : 'Change'}
        </span>
      </button>

      {open ? (
        <>
          <div className="signal-picker__scrim" onClick={close} aria-hidden="true" />
          <div className="signal-picker__panel">
            <div className="signal-picker__search">
              <input
                ref={inputRef}
                type="search"
                className="form-input"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-label={`Search the signals your boat reports for ${label.toLowerCase()}`}
                aria-activedescendant={
                  flat[activeIndex] === undefined ? undefined : optionId(activeIndex)
                }
                placeholder="Search by name, path or device"
                autoComplete="off"
                value={query}
                data-testid={`${testidPrefix}-search`}
                onChange={(e) => onSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
              />
              <button type="button" className="signal-picker__cancel" onClick={close}>
                Cancel
              </button>
            </div>

            <ul className="signal-picker__list" id={listId} role="listbox" aria-label={label}>
              {grouped.map(({ group, rows }) => (
                <li key={group} className="signal-picker__group" role="presentation">
                  <span className="signal-picker__group-name">{group}</span>
                  <ul className="signal-picker__rows" role="group" aria-label={group}>
                    {rows.map((signal) => {
                      const index = indexByPath.get(signal.path) ?? 0;
                      const reading = renderReading(signal);
                      const device =
                        signal.deviceName === null || signal.deviceName === ''
                          ? ''
                          : ` (${signal.deviceName})`;
                      return (
                        <li
                          key={signal.path}
                          id={optionId(index)}
                          role="option"
                          aria-selected={signal.path === value}
                          aria-label={`${labelOf(signal)}${device}, ${reading.text}, ${reading.seen}`}
                          className={`signal-row${index === activeIndex ? ' signal-row--active' : ''}${
                            signal.path === value ? ' signal-row--picked' : ''
                          }`}
                          data-testid={`${testidPrefix}-row-${signal.path}`}
                          ref={(el) => {
                            if (el === null) rowRefs.current.delete(signal.path);
                            else rowRefs.current.set(signal.path, el);
                          }}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => pick(signal)}
                        >
                          <span className="signal-row__main">
                            <span className="signal-row__label">
                              {labelOf(signal)}
                              {device === '' ? null : (
                                <span className="signal-row__device">{device}</span>
                              )}
                            </span>
                            <span className="signal-row__path">{signal.path}</span>
                          </span>
                          <span className="signal-row__live" title={reading.seen}>
                            <span
                              className={`boat-status-dot boat-status-dot--${reading.dot}`}
                              aria-hidden="true"
                            />
                            <span className="signal-row__value">{reading.text}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>

            {flat.length === 0 ? (
              <p className="signal-picker__empty" data-testid={`${testidPrefix}-empty`}>
                {query.trim() === ''
                  ? 'This boat has not reported any signals yet.'
                  : `Nothing your boat reports matches “${query.trim()}”. Try part of the name, the path, or the device it comes from.`}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
